import { ConsoleLogger } from '@proposify/frontend-observability';
import calculateSignaturePosition from 'components/editor/helpers/calculateSignaturePosition';
import { defaultBlockStyle } from 'components/editor/hooks/useBlockSettings';
import { useSignaturesContext } from 'components/editor/providers/SignaturesProvider.tsx';
import { useSectionData } from 'components/editor/Sections/useSectionContext';
import { GridBlockType } from 'services/entities/BlockTypes';
import { getCanvasWidth } from 'components/editor/shared/gridConfig.ts';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import { copyBlockToClipboard, readBlockFromClipboard } from 'components/editor/utils/clipboard';
import SvgIcoCopyComment from 'components/icon/icons/IcoCopyComment';
import SvgIcoPasteComment from 'components/icon/icons/IcoPasteComment';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { setCanPaste, setLastCopiedVariable } from 'components/editor/grid/reduxStore/contextMenuSlice';
import checkIsMac from 'components/editor/helpers/checkIsMac';
import { useCreateBlockWithSettings } from 'components/editor/hooks/useCreateBlockWithSettings';
import useActiveSection from 'components/editor/SectionsDrawer/useActiveSection.tsx';
import type { SerializeForClipboardProps } from 'components/editor/utils/clipboard.ts';
import SvgIcoCopyCommentWin from 'components/icon/icons/IcoCopyCommentWin';
import SvgIcoPasteCommentWin from 'components/icon/icons/IcoPasteCommentWin';
import { openNotification } from 'components/notification/index.tsx';
import { useSocketClient } from 'providers/SocketContext.tsx';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useTranslation } from 'react-i18next';
import type { UnSignedSignatureBox } from 'services/entities/SignatureTypes';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useAppDispatch, useAppSelector } from 'components/editor/grid/reduxStore/Store';
import type { KeyboardShortcut } from '../../../hooks/useKeyboardShortcuts';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';
import { SelectionContext } from '../../SelectedBlockInfoProvider.tsx';
import { useActiveEditor } from '../../TiptapEditor/ActiveEditorContext.tsx';
import type { CustomContextMenuOptions } from '../CustomContextMenuContainer.tsx';
import { calculateRelativePosition, getContentOption } from './pasteOperationUtils.ts';
import { removeCommentMarksFromContent } from './removeCommentMarksFromContent.ts';

type Position = {
  x: number;
  y: number;
};

type GetPositionFn = () => Promise<Position>;

type HandlePasteParams = {
  getPosition: GetPositionFn;
};

export const useBlockClipboardOptions = (disabled = false): CustomContextMenuOptions => {
  const canvasWidth = getCanvasWidth(useEffectiveOrientation());
  const { activeSection: activeSectionId } = useActiveSection();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const { selectedSignature, sectionsSignatures, handleSignatureInsert } = useSignaturesContext();

  const { lastContextMenuPosition } = useSelector((state: RootState) => state.contextMenu);
  const blocksSettings = useSelector((state: RootState) => state.blockStyleSettings);
  const { socketClient } = useSocketClient();
  const isSocketConnected = socketClient.getIsConnected();
  const { t } = useTranslation();
  const { isEditMode } = useActiveEditor();

  const dispatch = useAppDispatch();
  const canPaste = useSelector((state: RootState) => state.contextMenu.canPaste);
  const contentId = useAppSelector(selectContentId);

  const [isBlockCopyEnable, setIsBlockCopyEnable] = useState(false);
  const { blocksContent, getSectionBlocksLayer, activeSectionContent } = useSectionData();
  const createBlockWithSettings = useCreateBlockWithSettings();
  const isOnMac = checkIsMac();

  const handleCopy = async () => {
    if (selectedBlockIdByWrapper) {
      await copyAllBlocks();
    } else if (selectedSignature) {
      await copyFromSignatureProvider();
    }
  };

  const copyAllBlocks = async () => {
    // when not fully scroll to the section sectionContent is not updated so we need to check both blocksContent and activeSectionContent
    const blockToCopy = selectedBlockIdByWrapper
      ? activeSectionContent?.[selectedBlockIdByWrapper] || blocksContent[selectedBlockIdByWrapper]
      : null;

    if (!blockToCopy) {
      return false;
    }

    let cleanedContent = blockToCopy;
    if (blockToCopy.content && typeof blockToCopy.content === 'string') {
      try {
        const contentJson = JSON.parse(blockToCopy.content);
        const cleanedJson = removeCommentMarksFromContent(contentJson);
        cleanedContent = {
          ...blockToCopy,
          content: JSON.stringify(cleanedJson),
        };
      } catch {
        cleanedContent = blockToCopy;
      }
    }

    const blockStyleSettings = blocksSettings.blockStyle[blockToCopy.blockConfig?.id] ?? null;

    const clipboardPayload = {
      data: cleanedContent,
      styles: blockStyleSettings,
      sourceContentId: contentId,
      sectionId: activeSectionId,
    };
    const success = await copyBlockToClipboard(clipboardPayload);
    if (success) {
      dispatch(setCanPaste(true));
      dispatch(setLastCopiedVariable(null));
    }
    return success;
  };

  const copyFromSignatureProvider = async () => {
    const isSignatureSelectedId = selectedSignature?.sectionId;

    if (!isSignatureSelectedId) {
      return false;
    }
    const blockToCopy = sectionsSignatures[isSignatureSelectedId]?.find((sig) => sig.signatureBoxId === selectedSignature.signatureId);

    if (!blockToCopy) {
      return false;
    }

    blockToCopy.signatureBoxId = '';

    const clipboardPayload = {
      data: blockToCopy,
      sourceContentId: contentId,
      sectionId: activeSectionId,
    };

    const success = await copyBlockToClipboard(clipboardPayload);
    if (success) {
      dispatch(setCanPaste(true));
      dispatch(setLastCopiedVariable(null));
    }
    return success;
  };

  const handlePaste = async ({ getPosition }: HandlePasteParams) => {
    if (!isSocketConnected) {
      openNotification({
        type: 'warning',
        title: t('document.grid.copy_paste.warning.socket_disconnection.title'),
        description: t('document.grid.copy_paste.warning.socket_disconnection.description'),
      });
      return;
    }

    const clipboardData = await readBlockFromClipboard();

    if (!clipboardData?.data) return ConsoleLogger.error('Clipboard data is null');

    const pastedData = clipboardData.data;

    const relativePosition = await getPosition();
    if ('type' in pastedData && pastedData.type === GridBlockType.SIGNATURE) {
      pasteSignatureBlock({ relativePosition, pastedData: pastedData as unknown as UnSignedSignatureBox, clipboardData });
    } else if ('blockConfig' in pastedData) {
      const blockConfig = {
        x: relativePosition.x,
        y: relativePosition.y,
        z: getSectionBlocksLayer().greaterZIndexAvailable,
        width: pastedData.blockConfig.width,
        height: pastedData.blockConfig.height,
      };
      await createBlockWithSettings({
        itemType: pastedData.type,
        blockConfig,
        sectionId: activeSectionId,
        content: getContentOption(pastedData),
        blockStyleSettings: clipboardData.styles || defaultBlockStyle,
      });
    }
  };

  const pasteFromContextMenu = () => {
    if (!lastContextMenuPosition?.editorRect) return;

    return handlePaste({
      getPosition: async () => ({
        x: lastContextMenuPosition.x - lastContextMenuPosition.editorRect.left,
        y: lastContextMenuPosition.y - lastContextMenuPosition.editorRect.top,
      }),
    });
  };

  const pasteFromKeyboard = () => {
    return handlePaste({
      getPosition: async () => {
        const clipboardData = await readBlockFromClipboard();
        return calculateRelativePosition(clipboardData as SerializeForClipboardProps, activeSectionId);
      },
    });
  };

  const pasteSignatureBlock = ({
    relativePosition,
    pastedData,
    clipboardData,
  }: {
    relativePosition: { x: number; y: number };
    pastedData: UnSignedSignatureBox;
    clipboardData: SerializeForClipboardProps;
  }) => {
    const sourceContentId = clipboardData.sourceContentId;
    const isAssignedAndPastedIntoDiffDocs = sourceContentId && sourceContentId !== contentId && pastedData.assignedSignee;

    if (isAssignedAndPastedIntoDiffDocs) {
      pastedData.assignedSignee = null;
      openNotification({
        type: 'warning',
        title: t('document.grid.copy_paste.warning.pasting_signature_into_diff_docs.title'),
        description: t('document.grid.copy_paste.warning.pasting_signature_into_diff_docs.description'),
      });
    }

    const boundingBox = { width: lastContextMenuPosition?.editorRect?.width ?? canvasWidth };
    const itemWidth = pastedData.properties.dimensions.width;

    const { x: adjustedX } = calculateSignaturePosition(relativePosition.x, itemWidth, boundingBox, { x: 0 }, { x: 0 });

    const payload: UnSignedSignatureBox & { sectionId?: string } = {
      ...pastedData,
      properties: {
        dimensions: pastedData.properties.dimensions,
        position: {
          x: adjustedX,
          y: relativePosition.y,
        },
      },
      sectionId: activeSectionId,
    };
    handleSignatureInsert(activeSectionId || '', payload);
  };

  useEffect(() => {
    const hasSelectedSignature = selectedSignature;
    const isBlockCopyEnabled = !!(selectedBlockIdByWrapper && !isEditMode);
    const isSignatureCopyEnabled = hasSelectedSignature?.signatureId !== null;
    setIsBlockCopyEnable(isBlockCopyEnabled || isSignatureCopyEnabled);
  }, [selectedBlockIdByWrapper, selectedSignature, isEditMode]);

  const keyboardShortcuts: KeyboardShortcut[] = [
    {
      key: 'c',
      ctrlOrCmd: true,
      callback: async () => await handleCopy(),
      isEnable: isBlockCopyEnable,
    },
    {
      key: 'v',
      ctrlOrCmd: true,
      callback: async () => await pasteFromKeyboard(),
      isEnable: !isEditMode,
    },
  ];

  useKeyboardShortcuts(keyboardShortcuts);

  return [
    {
      type: 'item' as const,
      title: 'Copy',
      callback: handleCopy,
      icon: !isOnMac ? <SvgIcoCopyCommentWin width={40} height={20} /> : <SvgIcoCopyComment width={20} height={20} />,
      disabled: disabled || !isBlockCopyEnable,
    },
    {
      type: 'item' as const,
      title: 'Paste',
      callback: pasteFromContextMenu,
      icon: !isOnMac ? <SvgIcoPasteCommentWin width={36} height={16} /> : <SvgIcoPasteComment width={20} height={20} />,
      disabled: disabled || !canPaste,
    },
  ];
};
